import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getDatabase, ref, push, update, get } from "firebase/database";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { getUserByID, sendSMS, sendEmail } from "../utils";
import {
  notifyRenterBookingEditProposed,
} from "../utils/userNotifications";

dayjs.extend(customParseFormat);

// Helper function to calculate renter fee (straight 5%, no cap)
// For fishing charters (boatCaptain === true): charge 5% platform fee as renter fee
// For regular rentals (boatCaptain === false): charge 5% renter fee
// Must match PaymentForm.js logic exactly
const calculateRenterFee = (amount, listingData = null) => {
    if (listingData?.boatCaptain === true) {
        // Fishing charter: charge 5% platform fee as renter fee
        return 0.05 * amount;
    }
    // Regular rental: charge 5% renter fee
    return 0.05 * amount;
};

// Helper function to determine if a rental is taxable
// Tax (8.25%) is ONLY collected for rentals in Texas that are driven by the renter (no captain).
// No tax for: Texas charters/captain-driven rentals, or ANY rental outside of Texas.
// Must match PaymentForm.js logic exactly
const checkIfRentalIsTaxable = async (listingData) => {
    try {
        // boatCaptain === true means only owner/captain drives (charter) => NOT taxable
        if (listingData?.boatCaptain === true) {
            return false;
        }

        const lat = listingData?.storageAddress?.lat;
        const lng = listingData?.storageAddress?.lng;
        if (!lat || !lng) {
            return false;
        }

        const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBfEcoyW9DM7QQWdV3oTjevrfyhX5n5qqg`
        );
        const data = await response.json();
        const addressComponents = data.results[0]?.address_components || [];
        const stateComponent = addressComponents.find((component) =>
            component.types.includes("administrative_area_level_1")
        );
        const state = stateComponent?.short_name || "";
        return state === "TX";
    } catch (error) {
        console.error("Error checking tax eligibility:", error);
        return false;
    }
};

export default function EditBookingModal({
    show,
    onHide,
    bookingData,
    inquiryData,
    boatDetails,
    paymentDetails,
    currentUser,
    onUpdateSuccess
}) {
    const { dynamicId } = useParams();


    // State
    const [newTripDate, setNewTripDate] = useState(null);
    const [newTripTime, setNewTripTime] = useState(null);
    const [passengerCounts, setPassengerCounts] = useState({
        adults: 0,
        children: 0,
        infants: 0,
    });
    const [editLoading, setEditLoading] = useState(false);
    
    // Direct Update / Refund States
    const [modalStep, setModalStep] = useState(1);
    const [directUpdateData, setDirectUpdateData] = useState(null);
    const [directUpdateLoading, setDirectUpdateLoading] = useState(false);
    const [initialDeps, setInitialDeps] = useState(null);

    // Duration Editing State
    const [hourlyPackages, setHourlyPackages] = useState([]);
    const [dailyPackages, setDailyPackages] = useState([]);
    const [tripPackages, setTripPackages] = useState([]);
    const [selectedDurationOption, setSelectedDurationOption] = useState(null);
    const [customHours, setCustomHours] = useState(0);
    const [manualPrice, setManualPrice] = useState(0);
    const [isTaxable, setIsTaxable] = useState(false);

    // Snapshot for History
    const [originalSnapshot, setOriginalSnapshot] = useState({
        date: null,
        time: null,
        passengers: 0,
        price: 0,
        duration: ""
    });

    // Initialization Effect
    useEffect(() => {

        console.log("Inquiry Data", inquiryData)
        if (show && bookingData) {
            // 1. Initialize Date/Time
            let initialDate = bookingData.tripDate;
            let initialTime = bookingData.tripTime;

            if (!initialDate) {
                if (inquiryData?.offerData?.offeredDate) initialDate = inquiryData.offerData.offeredDate;
                else if (inquiryData?.inquiryData?.prefferedDate) initialDate = inquiryData.inquiryData.prefferedDate;
            }

            if (!initialTime) {
                if (inquiryData?.offerData?.offeredTime) initialTime = inquiryData.offerData.offeredTime;
                else if (inquiryData?.inquiryData?.prefferedTime) initialTime = inquiryData.inquiryData.prefferedTime;
            }

            // Try parsing Date (Supporting "Tuesday, March 3, 2026" and standard formats)
            let parsedDate = dayjs(initialDate, ["MMMM D, YYYY", "dddd, MMMM D, YYYY", "YYYY-MM-DD"]);
            if (!parsedDate.isValid()) parsedDate = dayjs(initialDate);
            setNewTripDate(parsedDate);

            // Try parsing Time (Supporting "01:00", "01:00:am", "1:00 AM", etc.)
            let parsedTime = dayjs(initialTime, ["h:mm A", "hh:mm A", "h:mm a", "hh:mm a", "h:mm:a", "hh:mm:a", "h:mmA", "hh:mmA", "HH:mm", "HH:mm:ss"]);
            if (!parsedTime.isValid()) parsedTime = dayjs(initialTime); // Fallback to loose parsing
            setNewTripTime(parsedTime.isValid() ? parsedTime : dayjs());

            // 2. Initialize Passengers
            let src = bookingData.passengers || {};
            if (!src.adults && bookingData.inquiryData && (bookingData.inquiryData.adults || bookingData.inquiryData.total)) {
                src = bookingData.inquiryData;
            }
            if (!src.adults && inquiryData && inquiryData.inquiryData) {
                src = inquiryData.inquiryData;
            }

            const passengerCountsObj = {
                adults: Number(src.adults) || 0,
                children: Number(src.children) || 0,
                infants: Number(src.infants) || 0,
            };
            setPassengerCounts(passengerCountsObj);

            // 3. Initialize Price (Important: Do this before potential recalculations)
            let initialPrice = Number(bookingData.priceOffered);
            if (!initialPrice && bookingData.offerData && bookingData.offerData.offerPrice) {
                initialPrice = Number(bookingData.offerData.offerPrice);
            }
            if (!initialPrice && inquiryData && inquiryData.offerData && inquiryData.offerData.offerPrice) {
                initialPrice = Number(inquiryData.offerData.offerPrice);
            }
            setManualPrice(initialPrice);

            // 4. Parse Duration Options
            let packages = boatDetails?.fishingGuideDetails?.tripPackages;

            // Determine taxability on load
            if (boatDetails) {
                checkIfRentalIsTaxable(boatDetails).then(res => setIsTaxable(res));
            }
            if (typeof packages === 'string') {
                try { packages = JSON.parse(packages); } catch (e) { console.error("Error parsing fishingPackages", e); }
            }

            const parsedTripPackages = [];
            if (packages && Array.isArray(packages)) {
                packages.forEach(pkg => {
                    const labelName = pkg.name || pkg.packageName || "Package";
                    const durationStr = String(pkg.duration || "");
                    const durationText = durationStr.toLowerCase().includes('hour') ? durationStr : `${durationStr} hours`;

                    parsedTripPackages.push({
                        label: `${labelName} (${durationText})`,
                        price: Number(pkg.basePrice),
                        type: 'package',
                        duration: durationText,
                        raw: pkg
                    });
                });
            }

            let hourly = boatDetails?.pricing?.hourlyOptions;
            if (typeof hourly === 'string') {
                try { hourly = JSON.parse(hourly); } catch (e) { console.error("Error parsing hourlyOptions", e); }
            }

            const parsedHourlyPackages = [];
            if (hourly && Array.isArray(hourly)) {
                hourly.forEach(opt => {
                    const parsedHours = Number(opt.hours);
                    const hUnit = parsedHours === 1 ? "Hour" : "Hours";
                    parsedHourlyPackages.push({
                        label: `${parsedHours} ${hUnit}`,
                        price: Number(opt.price),
                        type: 'hourly',
                        duration: `${parsedHours} ${hUnit}`,
                        hours: parsedHours,
                        raw: opt
                    });
                });
            }

            let daily = boatDetails?.pricing?.dailyOptions;
            if (typeof daily === 'string') {
                try { daily = JSON.parse(daily); } catch (e) { console.error("Error parsing dailyOptions", e); }
            }

            const parsedDailyPackages = [];
            if (daily && Array.isArray(daily)) {
                daily.forEach(opt => {
                    const dUnit = Number(opt.days) === 1 ? "Day" : "Days";
                    parsedDailyPackages.push({
                        label: `${opt.days} ${dUnit} (${opt.days * 24} hours)`,
                        price: Number(opt.price),
                        type: 'daily',
                        duration: `${opt.days} ${dUnit}`,
                        days: Number(opt.days),
                        raw: opt
                    });
                });
            }

            setTripPackages(parsedTripPackages);
            setHourlyPackages(parsedHourlyPackages);
            setDailyPackages(parsedDailyPackages);

            // 5. Smart Duration Logic - Find Matching Package
            let initialCustomHours = 0;
            let initialSelectedOption = null;
            let currentDurationHours = 0;
            let currentDurationDays = 0;
            let currentDurationText = "";

            if (typeof bookingData.duration === 'object' && bookingData.duration) {
                if (bookingData.duration.sameDayHours) {
                    currentDurationHours = Number(bookingData.duration.sameDayHours);
                }
                if (bookingData.duration.overnight) {
                    currentDurationDays = Number(bookingData.duration.overnight);
                }
                // Try to get text representation
                currentDurationText = bookingData.duration.text || bookingData.duration.duration || "";
                if (!currentDurationHours && !currentDurationDays && currentDurationText) {
                    if (currentDurationText.toLowerCase().includes('day') || currentDurationText.toLowerCase().includes('night')) {
                        const match = currentDurationText.match(/(\d+)/);
                        if (match) currentDurationDays = Number(match[1]);
                    } else {
                        const match = currentDurationText.match(/(\d+)/);
                        if (match) currentDurationHours = Number(match[1]);
                    }
                }
            } else if (typeof bookingData.duration === 'string') {
                currentDurationText = bookingData.duration;
                if (currentDurationText.toLowerCase().includes('day') || currentDurationText.toLowerCase().includes('night')) {
                    const match = currentDurationText.match(/(\d+)/);
                    if (match) currentDurationDays = Number(match[1]);
                } else {
                    const match = currentDurationText.match(/(\d+)/);
                    if (match) currentDurationHours = Number(match[1]);
                }
            } else if (bookingData.offerData && bookingData.offerData.duration) {
                currentDurationText = bookingData.offerData.duration;
                if (currentDurationText.toLowerCase().includes('day') || currentDurationText.toLowerCase().includes('night')) {
                    const match = currentDurationText.match(/(\d+)/);
                    if (match) currentDurationDays = Number(match[1]);
                } else {
                    const match = currentDurationText.match(/(\d+)/);
                    if (match) currentDurationHours = Number(match[1]);
                }
            } else if (inquiryData) {
                // Fallback to inquiryData
                if (inquiryData.offerData && inquiryData.offerData.duration) {
                    currentDurationText = inquiryData.offerData.duration;
                    if (currentDurationText.toLowerCase().includes('day') || currentDurationText.toLowerCase().includes('night')) {
                        const match = currentDurationText.match(/(\d+)/);
                        if (match) currentDurationDays = Number(match[1]);
                    } else {
                        const match = currentDurationText.match(/(\d+)/);
                        if (match) currentDurationHours = Number(match[1]);
                    }
                } else if (inquiryData.inquiryData && inquiryData.inquiryData.overnight) {
                    currentDurationDays = Number(inquiryData.inquiryData.overnight);
                    currentDurationText = `${currentDurationDays} ${currentDurationDays === 1 ? "Day" : "Days"}`;
                } else if (inquiryData?.inquiryData?.sameDayHours) {
                    currentDurationHours = Number(inquiryData.inquiryData.sameDayHours);
                    currentDurationText = `${currentDurationHours} ${currentDurationHours === 1 ? "Hour" : "Hours"}`;
                }
            }

            // 0. Check Daily Packages first explicitly
            let foundDailyPackage = null;
            if (currentDurationDays > 0) {
                const matchingDailyPackages = parsedDailyPackages.filter(opt => opt.days === currentDurationDays);
                if (matchingDailyPackages.length > 0) {
                    // Find explicit price match or just default to the corresponding day option
                    foundDailyPackage = matchingDailyPackages.find(opt => Math.abs(Number(opt.price) - initialPrice) < 1) || matchingDailyPackages[0];
                }
            }

            // A. Check Trip Packages first if no daily found 
            let foundTripPackage = null;
            if (!foundDailyPackage) {
                const matchingTripPackages = parsedTripPackages.filter(opt => {
                    const optMatch = opt.duration ? opt.duration.match(/(\d+)/) : null;
                    const optHours = optMatch ? Number(optMatch[1]) : 0;
                    return optHours > 0 && optHours === currentDurationHours;
                });

                if (matchingTripPackages.length > 0) {
                    // Try strict price match first
                    foundTripPackage = matchingTripPackages.find(opt =>
                        Math.abs(Number(opt.price) - initialPrice) < 1
                    );
                    if (!foundTripPackage) {
                        foundTripPackage = matchingTripPackages[0];
                    }
                }
            }

            if (foundDailyPackage) {
                initialSelectedOption = foundDailyPackage;
                initialCustomHours = 0;
            } else if (foundTripPackage) {
                initialSelectedOption = foundTripPackage;
                initialCustomHours = 0;
            } else {
                // B. Check Hourly Packages if no matched trip package
                const foundHourlyPackage = parsedHourlyPackages.find(opt => opt.hours === currentDurationHours);

                if (foundHourlyPackage) {
                    initialSelectedOption = foundHourlyPackage;
                    initialCustomHours = 0;
                } else {
                    // C. Fallback to Custom Hours
                    initialCustomHours = currentDurationHours;
                    initialSelectedOption = null;
                }
            }

            setCustomHours(initialCustomHours);
            setSelectedDurationOption(initialSelectedOption);
            setInitialDeps({
                duration: initialSelectedOption,
                hours: initialCustomHours,
                pax: passengerCountsObj
            });

            // 6. Set Original Snapshot for History
            const initialTotalPassengers = (Number(passengerCountsObj.adults) || 0) + (Number(passengerCountsObj.children) || 0) + (Number(passengerCountsObj.infants) || 0);

            setOriginalSnapshot({
                date: initialDate,
                time: initialTime,
                passengers: initialTotalPassengers,
                price: initialPrice,
                duration: currentDurationText || (currentDurationHours ? `${currentDurationHours} ${currentDurationHours === 1 ? "Hour" : "Hours"}` : "")
            });
        }
    }, [show, bookingData, boatDetails]);


    const calculateNewPrice = () => {
        if (!bookingData) return null;

        let basePrice = Number(bookingData.priceOffered);
        if (selectedDurationOption) {
            basePrice = Number(selectedDurationOption.price);
        } else if (customHours > 0) {
            basePrice = (Number(boatDetails?.pricing?.hourlyRate) || 0) * customHours;
        }

        if (!boatDetails?.pricing) {
            return basePrice;
        }

        let maxPassengers = Number(boatDetails.pricing?.maxPassengers) || 6;
        if (selectedDurationOption?.raw?.maxPassengers !== undefined && selectedDurationOption?.raw?.maxPassengers !== "") {
            maxPassengers = Number(selectedDurationOption.raw.maxPassengers);
        }

        let additionalPassengerPercentage = 0;
        if (selectedDurationOption?.raw?.additionalPassengerPercentage !== undefined && selectedDurationOption?.raw?.additionalPassengerPercentage !== "") {
            additionalPassengerPercentage = Number(selectedDurationOption.raw.additionalPassengerPercentage);
        } else if (boatDetails?.boatType === "Fishing Charter") {
            additionalPassengerPercentage = Number(boatDetails.pricing?.additionalPassengerPercentage) || 0;
        }

        const totalPassengers =
            (Number(passengerCounts.adults) || 0) +
            (Number(passengerCounts.children) || 0) +
            (Number(passengerCounts.infants) || 0);

        let extraPassengers = Math.max(0, totalPassengers - maxPassengers);
        if ((boatDetails?.boatType === "Bass Boat" || boatDetails?.boatType === "Fishing Charter") && selectedDurationOption?.type === "daily") {
            extraPassengers = 0;
        }

        if (extraPassengers > 0 && additionalPassengerPercentage > 0) {
            const factor = (additionalPassengerPercentage / 100);

            if (selectedDurationOption || customHours > 0) {
                const newExtraPrice = (basePrice * factor * extraPassengers);
                return basePrice + newExtraPrice;
            }

            const oldTotalPassengers = (bookingData.passengers?.total || 0);
            const oldExtra = Math.max(0, oldTotalPassengers - maxPassengers);

            const estimatedBasePrice = basePrice / (1 + (factor * oldExtra));
            const newExtraPrice = (estimatedBasePrice * factor * extraPassengers);
            return estimatedBasePrice + newExtraPrice;
        }

        return basePrice;
    };

    useEffect(() => {
        if (!bookingData || !show) return;

        // Prevent overwriting the manual price on initial mount if nothing has changed
        if (initialDeps &&
            JSON.stringify(selectedDurationOption) === JSON.stringify(initialDeps.duration) &&
            customHours === initialDeps.hours &&
            JSON.stringify(passengerCounts) === JSON.stringify(initialDeps.pax)) {
            return;
        }

        const calculated = calculateNewPrice();
        if (calculated !== null && calculated !== undefined) {
            setManualPrice(calculated);
        }
    }, [selectedDurationOption, customHours, passengerCounts, bookingData, show, initialDeps]);


    const handleUpdateBooking = async () => {
        setEditLoading(true);
        try {
            const database = getDatabase();

            const formattedDate = newTripDate ? dayjs(newTripDate).format("MMMM D, YYYY") : bookingData.tripDate;
            const formattedTime = newTripTime ? dayjs(newTripTime).format("HH:mm") : bookingData.tripTime;
            const totalPassengers =
                (Number(passengerCounts.adults) || 0) +
                (Number(passengerCounts.children) || 0) +
                (Number(passengerCounts.infants) || 0);

            const safetyCapacity = Number(boatDetails.capacity) || 100;
            if (totalPassengers > safetyCapacity) {
                toast.error(`Total passengers cannot exceed boat capacity (${safetyCapacity})`);
                setEditLoading(false);
                return;
            }

            const finalPrice = Number(manualPrice);

            // 0. Track Changes for History
            const changes = [];
            const timestamp = Date.now();
            const updatedBy = currentUser?.email || currentUser?.uid || "Admin/System";

            // Date
            if (originalSnapshot.date !== formattedDate) {
                changes.push({ field: "Trip Date", old: originalSnapshot.date || "", new: formattedDate || "" });
            }
            // Time
            if (originalSnapshot.time !== formattedTime) {
                changes.push({ field: "Trip Time", old: originalSnapshot.time || "", new: formattedTime || "" });
            }
            // Passengers
            const oldTotal = originalSnapshot.passengers || 0;
            if (oldTotal !== totalPassengers) {
                changes.push({ field: "Total Passengers", old: oldTotal, new: totalPassengers });
            }
            // Price
            if (Math.abs(Number(originalSnapshot.price || 0) - Number(finalPrice || manualPrice || 0)) > 0.01) {
                changes.push({ field: "Price", old: Number(originalSnapshot.price || 0), new: Number(finalPrice || manualPrice || 0) });
            }

            // Duration Comparison
            let newDurationStr = "";
            if (selectedDurationOption) {
                newDurationStr = selectedDurationOption.duration || "";
            } else if (customHours > 0) {
                newDurationStr = `${customHours} ${customHours === 1 ? "Hour" : "Hours"}`;
            } else {
                if (typeof bookingData.duration === 'object' && bookingData.duration) {
                    newDurationStr = bookingData.duration.text || bookingData.duration.duration || `${bookingData.duration.sameDayHours || 0} ${bookingData.duration.sameDayHours === 1 ? "Hour" : "Hours"}`;
                } else {
                    newDurationStr = String(bookingData.duration || "");
                }
            }

            const oldDurationStr = originalSnapshot.duration || "";

            const normalizeDuration = (str) => {
                if (!str) return "";
                let s = String(str).trim().toLowerCase();
                s = s.replace(/'s\b/g, "").replace(/\s+/g, " ");
                s = s.replace(/^1\s+hours$/, "1 hour").replace(/^1\s+days$/, "1 day").replace(/^1\s+nights$/, "1 night");
                return s;
            };

            if (oldDurationStr && newDurationStr && normalizeDuration(oldDurationStr) !== normalizeDuration(newDurationStr)) {
                changes.push({ field: "Duration", old: oldDurationStr, new: newDurationStr });
            }


            const updates = {};

            if (changes.length === 0) {
                toast.info("No changes were made to the booking.");
                setEditLoading(false);
                return;
            }

            const hasOtherChanges = changes.some(c => c.field !== "Price");

            if (changes.length > 0) {
                const historyEntry = {
                    timestamp,
                    updatedBy,
                    changes
                };
                const newHistoryKey = push(ref(database, `bookings/${dynamicId}/history`)).key;
                updates[`bookings/${dynamicId}/history/${newHistoryKey}`] = historyEntry;
            }

            // 2. Prepare Booking Updates
            const bookingPath = `bookings/${dynamicId}`;
            updates[`${bookingPath}/passengers`] = {
                ...passengerCounts,
                total: totalPassengers
            };
            updates[`${bookingPath}/priceOffered`] = finalPrice;
            updates[`${bookingPath}/tripDate`] = formattedDate;
            updates[`${bookingPath}/tripTime`] = formattedTime;

            // Update Duration
            if (selectedDurationOption) {
                let durationObj = {};
                if (selectedDurationOption.type === 'daily') {
                    durationObj = { sameDayHours: 0, overnight: selectedDurationOption.days, text: selectedDurationOption.label };
                } else {
                    const dText = selectedDurationOption.duration || "";
                    const hMatch = dText.match(/(\d+)\s*hour/i);
                    if (hMatch) {
                        durationObj = { sameDayHours: parseInt(hMatch[1]), overnight: 0 };
                    } else {
                        durationObj = { sameDayHours: 0, text: dText };
                    }
                }
                updates[`${bookingPath}/duration`] = durationObj;
            } else if (customHours > 0) {
                updates[`${bookingPath}/duration`] = { sameDayHours: customHours, overnight: 0 };
            }

            // 3. Prepare Inquiry Updates
            if (bookingData.inquiryId) {
                const inquiryPath = `inquiries/${bookingData.inquiryId}`;
                updates[`${inquiryPath}/offerData/offeredDate`] = formattedDate;
                updates[`${inquiryPath}/offerData/offeredTime`] = formattedTime;
                updates[`${inquiryPath}/offerData/arivingDateTime`] = `${formattedDate} ${formattedTime}`;
                updates[`${inquiryPath}/offerData/offerPrice`] = finalPrice;
                updates[`${inquiryPath}/inquiryData/prefferedDate`] = formattedDate;
                updates[`${inquiryPath}/inquiryData/prefferedTime`] = formattedTime;
                updates[`${inquiryPath}/inquiryData/adults`] = passengerCounts.adults;
                updates[`${inquiryPath}/inquiryData/children`] = passengerCounts.children;
                updates[`${inquiryPath}/inquiryData/infants`] = passengerCounts.infants;
                updates[`${inquiryPath}/inquiryData/total`] = totalPassengers;

                if (selectedDurationOption) {
                    if (selectedDurationOption.type === 'daily') {
                        updates[`${inquiryPath}/offerData/duration`] = selectedDurationOption.label;
                        updates[`${inquiryPath}/offerData/offeredHours`] = 0;
                        updates[`${inquiryPath}/offerData/overnight`] = selectedDurationOption.days;
                        updates[`${inquiryPath}/inquiryData/overnight`] = selectedDurationOption.days;
                    } else {
                        const dText = selectedDurationOption.duration || "";
                        updates[`${inquiryPath}/offerData/duration`] = dText;
                        let durationObj = {};
                        const hMatch = dText.match(/(\d+)\s*hour/i);
                        if (hMatch) {
                            durationObj = { sameDayHours: parseInt(hMatch[1]), overnight: 0 };
                        }
                        updates[`${inquiryPath}/offerData/offeredHours`] = durationObj.sameDayHours || 0;
                        updates[`${inquiryPath}/offerData/overnight`] = 0;
                        updates[`${inquiryPath}/inquiryData/overnight`] = 0;
                    }
                } else if (customHours > 0) {
                    updates[`${inquiryPath}/offerData/duration`] = `${customHours} ${customHours === 1 ? "Hour" : "Hours"}`;
                    updates[`${inquiryPath}/offerData/offeredHours`] = customHours;
                    updates[`${inquiryPath}/offerData/overnight`] = 0;
                    updates[`${inquiryPath}/inquiryData/overnight`] = 0;
                }
            }

            // Declare these here so they are available for the proposedEdit object
            const priceChanged = changes.some(c => c.field === "Price");
            const oldPriceForDelta = Number(originalSnapshot.price || bookingData.priceOffered || 0);
            const newPriceCentsLocal = Math.round(finalPrice * 100);
            const oldPriceCentsLocal = Math.round(oldPriceForDelta * 100);
            const newRenterFeeCents = calculateRenterFee(newPriceCentsLocal, boatDetails) * 100;
            const oldRenterFeeCents = calculateRenterFee(oldPriceCentsLocal, boatDetails) * 100;
            const newTaxCents = isTaxable ? Math.round((8.25 / 100) * newPriceCentsLocal) : 0;
            const oldTaxCents = isTaxable ? Math.round((8.25 / 100) * oldPriceCentsLocal) : 0;
            
            const calculatedNewTotalCents = newPriceCentsLocal + newRenterFeeCents + newTaxCents;
            const calculatedOldTotalCents = oldPriceCentsLocal + oldRenterFeeCents + oldTaxCents;

            let amountDifferenceCents = priceChanged ? (calculatedNewTotalCents - calculatedOldTotalCents) : 0;
            let stripeChargeAmountCents = 0;
            let requiresImmediateStripeCharge = false;
            
            // Update Payment Details
            if (bookingData.paymentId && paymentDetails) {
                const paymentPath = `payments/${bookingData.paymentId}`;
                
                let newTotalAmountCents = paymentDetails.totalAmount || 0;
                let dbOwnerAmountCents = paymentDetails.ownerAmount || 0;
                let adminCutAmount = paymentDetails.adminCut || 0;
                let taxAmountCents = paymentDetails.totalTax || 0;
                let renterFeeAmount = calculateRenterFee(Number(originalSnapshot.price || 0) * 100, boatDetails) / 100;
                

                let newRemainingCents = paymentDetails.remainingAmount || 0;
                stripeChargeAmountCents = 0;
                requiresImmediateStripeCharge = false;

                const isCharterWithCashBalance = paymentDetails.charterDeposit && paymentDetails.collectBalanceAtMeetup;
                
                const isAdvanceBooking = 
                    paymentDetails.advanceBooking === 1 || 
                    paymentDetails.advanceBooking === true || 
                    paymentDetails.remainingAmount > 0 || 
                    paymentDetails.finalChargeAmount > 0 || 
                    (paymentDetails.paidAmount < paymentDetails.totalAmount && paymentDetails.status === 'paid');
                
                const is100PercentUpfront = !isAdvanceBooking;
                const hasFinalCharge = !!paymentDetails.finalChargeAmount;
                const isLowValueFullPayment = paymentDetails.isLowValueFullPayment;

                if (priceChanged) {
                    const finalPriceCents = Math.round(finalPrice * 100);
                    
                    const renterFeeCents = calculateRenterFee(finalPriceCents, boatDetails);
                    const taxAmountCentsLocal = isTaxable ? Math.round((8.25 / 100) * finalPriceCents) : 0;
                    
                    newTotalAmountCents = finalPriceCents + renterFeeCents + taxAmountCentsLocal - (paymentDetails.discount || 0);
                    taxAmountCents = taxAmountCentsLocal;
                    renterFeeAmount = renterFeeCents / 100;
                    
                    let cutPercentage = paymentDetails.cutPercentage !== undefined ? paymentDetails.cutPercentage : 0.1;
                    
                    let upfrontBaseAmount = finalPriceCents;
                    if (isAdvanceBooking && !hasFinalCharge && !isLowValueFullPayment) {
                        upfrontBaseAmount = Math.round(0.2 * finalPriceCents);
                    }
                    
                    adminCutAmount = renterFeeCents + Math.round(upfrontBaseAmount * cutPercentage);
                    
                    let firstRentalDeduction = 0;
                    if (paymentDetails.firstRentalPayment) {
                        if (paymentDetails.firstTimeSpecialRate) {
                            adminCutAmount = renterFeeCents + Math.round(upfrontBaseAmount * 0.5);
                        } else if (paymentDetails.adminFirstTimeCut) {
                            firstRentalDeduction = paymentDetails.adminFirstTimeCut;
                        } else if (paymentDetails.referrer && paymentDetails.referAmount) {
                            firstRentalDeduction = 4000;
                        } else {
                            firstRentalDeduction = 10000;
                        }
                        if (!paymentDetails.firstTimeSpecialRate) {
                            adminCutAmount += firstRentalDeduction;
                        }
                    }
                    
                    if (paymentDetails.charterDeposit) {
                        // Charter deposit: owner gets the deposit portion minus the admin % on the deposit only.
                        // This applies to BOTH collectBalanceAtMeetup (cash) and BBR-collects paths.
                        // Must match PaymentForm.js charter deposit ownerAmount logic.
                        const depositPercentage = paymentDetails.charterDepositPercentage || 20;
                        const charterChargeAmount = Math.round((depositPercentage / 100) * (finalPriceCents + taxAmountCentsLocal - (paymentDetails.discount || 0))) + renterFeeCents;
                        const depositPortion = charterChargeAmount - renterFeeCents;
                        const adminCutOnDeposit = Math.round(cutPercentage * depositPortion);
                        adminCutAmount = renterFeeCents + adminCutOnDeposit;
                        dbOwnerAmountCents = depositPortion - adminCutOnDeposit;
                    } else {
                        dbOwnerAmountCents = upfrontBaseAmount - adminCutAmount + renterFeeCents;
                    }
                }

                updates[`${paymentPath}/totalAmount`] = newTotalAmountCents;
                updates[`${paymentPath}/ownerAmount`] = dbOwnerAmountCents;
                updates[`${paymentPath}/adminCut`] = adminCutAmount;
                updates[`${paymentPath}/totalTax`] = taxAmountCents;
                
                // ---- STRIPE IMMEDIATE CHARGE/REFUND LOGIC START ----
                const oldTotalAmountCents = paymentDetails.totalAmount || 0;
                const deltaCents = priceChanged ? (newTotalAmountCents - oldTotalAmountCents) : 0;
                
                // Calculate how much was actually paid. Do NOT include priceAdjustments because paidAmount is now accurately updated on every edit.
                const totalActuallyPaidCents = (paymentDetails.paidAmount || 0) + (paymentDetails.finalChargeAmount || 0);
                
                const isPaidBooking = bookingData.status !== 'pending' && paymentDetails && 
                    (paymentDetails.status === 'paid' || Number(paymentDetails.paidAmount || 0) > 0 || Number(paymentDetails.finalChargeAmount || 0) > 0);

                if (priceChanged && isPaidBooking) {
                    let targetUpfrontAmountCents = newTotalAmountCents;

                    const finalPriceCentsLocal = Math.round(finalPrice * 100);

                    if (paymentDetails.charterDeposit) {
                        // Fishing Charters
                        const depositPercentage = paymentDetails.charterDepositPercentage || 20;
                        const baseAndTax = finalPriceCentsLocal + taxAmountCents - (paymentDetails.discount || 0);
                        targetUpfrontAmountCents = Math.round((depositPercentage / 100) * baseAndTax) + Math.round(renterFeeAmount * 100);
                    } else if (isAdvanceBooking && !hasFinalCharge && !isLowValueFullPayment) {
                        // Regular Advance Booking (>14 days)
                        const rentalDepositCents = Math.round(0.2 * finalPriceCentsLocal);
                        targetUpfrontAmountCents = rentalDepositCents + Math.round(renterFeeAmount * 100) + taxAmountCents - (paymentDetails.discount || 0);
                    }

                    if (targetUpfrontAmountCents < totalActuallyPaidCents) {
                        if (newTotalAmountCents < totalActuallyPaidCents) {
                            // They overpaid the NEW total. Must refund the excess above the total.
                            stripeChargeAmountCents = newTotalAmountCents - totalActuallyPaidCents; // Negative
                        } else {
                            // They paid more than the new target upfront, but less than the new total.
                            // No refund. Just apply to the remaining balance.
                            stripeChargeAmountCents = 0;
                        }
                    } else {
                        // Target upfront is greater than what they paid. We must charge the difference.
                        stripeChargeAmountCents = targetUpfrontAmountCents - totalActuallyPaidCents;
                    }

                    requiresImmediateStripeCharge = stripeChargeAmountCents !== 0;
                    newRemainingCents = Math.max(0, newTotalAmountCents - (totalActuallyPaidCents + stripeChargeAmountCents));
                } else {
                    stripeChargeAmountCents = 0;
                    requiresImmediateStripeCharge = false;
                    newRemainingCents = paymentDetails?.remainingAmount || 0;
                }

                amountDifferenceCents = deltaCents; // Used for UI layout to show price change

                // Record the actual Stripe charge/refund in the database so we don't double charge next time
                if (requiresImmediateStripeCharge && stripeChargeAmountCents !== 0) {
                    const currentAdjustmentsCents = (paymentDetails.priceAdjustments || 0) * 100;
                    updates[`${paymentPath}/priceAdjustments`] = (currentAdjustmentsCents + stripeChargeAmountCents) / 100;
                }

                if (paymentDetails.charterDeposit) {
                    updates[`${paymentPath}/charterCashBalance`] = paymentDetails.collectBalanceAtMeetup ? newRemainingCents : 0;
                    updates[`${paymentPath}/charterRemainingBalance`] = newRemainingCents;
                    if (!paymentDetails.collectBalanceAtMeetup) {
                        updates[`${paymentPath}/remainingAmount`] = newRemainingCents;
                    }
                    // Update charterDepositPaid to reflect the new deposit amount based on the new price
                    if (priceChanged) {
                        const depositPercentage = paymentDetails.charterDepositPercentage || 20;
                        const finalPriceCents = Math.round(finalPrice * 100);
                        const taxAmountCentsLocal = isTaxable ? Math.round((8.25 / 100) * finalPriceCents) : 0;
                        const renterFeeCents = calculateRenterFee(finalPriceCents, boatDetails);
                        const newCharterDepositPaid = Math.round((depositPercentage / 100) * (finalPriceCents + taxAmountCentsLocal - (paymentDetails.discount || 0))) + renterFeeCents;
                        updates[`${paymentPath}/charterDepositPaid`] = newCharterDepositPaid;
                    }
                } else {
                    updates[`${paymentPath}/remainingAmount`] = newRemainingCents;
                }
                
                // Sync pendingPayments if applicable
                if (bookingData.paymentId && ((paymentDetails.advanceBooking && !paymentDetails.isLowValueFullPayment) || (paymentDetails.charterDeposit && !paymentDetails.collectBalanceAtMeetup))) {
                    try {
                        const pendingPaymentRef = ref(database, `pendingPayments/${bookingData.paymentId}`);
                        const pendingSnapshot = await get(pendingPaymentRef);
                        if (pendingSnapshot.exists()) {
                            // Calculate new due date
                            const tripDateTimeObj2 = new Date(`${formattedDate} ${formattedTime}`);
                            let dueDate = new Date(tripDateTimeObj2);
                            dueDate.setDate(tripDateTimeObj2.getDate() - 2);
                            if (dueDate < new Date()) dueDate = new Date();
                            
                            updates[`pendingPayments/${bookingData.paymentId}/amountLeft`] = newRemainingCents;
                            updates[`pendingPayments/${bookingData.paymentId}/tripDate`] = formattedDate;
                            updates[`pendingPayments/${bookingData.paymentId}/dueDate`] = dueDate.toISOString();
                        }
                    } catch (e) { console.error("Error syncing pending payment amount:", e); }
                }
            } else if (bookingData.paymentId) {
                // Fallback
                const newTotalAmountCents = Math.round(finalPrice * 100);
                updates[`payments/${bookingData.paymentId}/totalAmount`] = newTotalAmountCents;
            }

            // Instead of executing the updates directly, we save them as a Proposed Edit
            const uid1 = currentUser?.uid;
            const uid2 = bookingData.renter;
            let chatId = "";
            if (uid1 && uid2) {
                chatId = uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
            }

            setDirectUpdateData({
                amountDifferenceCents: amountDifferenceCents || 0,
                stripeChargeAmountCents: stripeChargeAmountCents || 0,
                mainUpdates: updates,
                dynamicId,
                uid1,
                uid2,
                chatId,
                formattedDate,
                formattedTime,
                newPrice: finalPrice,
                passengerCounts,
                totalPassengers,
                requiresImmediateStripeCharge: requiresImmediateStripeCharge || false,
                changes,
                hasOtherChanges
            });
            setModalStep(2);
            setEditLoading(false);
            return;

        } catch (error) {
            console.error("Error preparing booking update:", error);
            toast.error("Failed: " + (error.message || "Unknown error"));
            setEditLoading(false);
        }
    };

    const executeFinalUpdate = async () => {
        if (!directUpdateData) return;
        setDirectUpdateLoading(true);
        try {
            const database = getDatabase();
            const { amountDifferenceCents, mainUpdates, dynamicId, uid1, uid2, chatId, formattedDate, formattedTime, newPrice, hasOtherChanges } = directUpdateData;

            // 2. Prepare Updates
            let chatMessages = [];
            
            // PROPOSED EDIT LOGIC
                const proposedEdit = {
                    date: directUpdateData.formattedDate,
                    time: directUpdateData.formattedTime,
                    passengers: { ...directUpdateData.passengerCounts, total: directUpdateData.totalPassengers },
                    newPrice: directUpdateData.newPrice,
                    deltaCents: amountDifferenceCents,
                    stripeChargeAmountCents: directUpdateData.stripeChargeAmountCents,
                    requiresStripeCharge: directUpdateData.requiresImmediateStripeCharge,
                    status: 'pending',
                    proposedBy: currentUser?.uid || "admin",
                    proposedAt: new Date().toISOString(),
                    firebaseUpdates: JSON.stringify(mainUpdates), // This contains the actual edits
                    changes: directUpdateData.changes // Store exact changes for Renter UI
                };

                const pendingUpdates = {};
                pendingUpdates[`bookings/${dynamicId}/proposedEdit`] = proposedEdit;

                if (chatId) {
                    const chatRef = ref(database, `chats/${chatId}`);
                    const chatSnapshot = await get(chatRef);
                    if (chatSnapshot.exists()) {
                        chatMessages = chatSnapshot.val().messages || [];
                    }
                    
                    const messageData = {
                        message: "The boat owner has proposed changes to your booking details. Please review them below.",
                        senderId: uid1,
                        receiverId: uid2,
                        timestamp: new Date().toISOString(),
                        type: "proposedEdit",
                        isUnread: true,
                        bookingId: dynamicId,
                        status: "pending",
                        proposedEditData: {
                            date: formattedDate || "",
                            time: formattedTime || "",
                            newPrice: newPrice || 0,
                            deltaCents: amountDifferenceCents,
                            changes: directUpdateData.changes || []
                        }
                    };
                    
                    if (chatSnapshot.exists()) {
                        pendingUpdates[`chats/${chatId}/messages`] = [...chatMessages, messageData];
                        pendingUpdates[`chats/${chatId}/lastMessage`] = "Proposed Booking Edit";
                        pendingUpdates[`chats/${chatId}/lastMessageTime`] = messageData.timestamp;
                        pendingUpdates[`chats/${chatId}/lastSenderId`] = uid1;
                        
                        if (chatSnapshot.val()?.senderId === uid1) {
                            pendingUpdates[`chats/${chatId}/senderReadStatus`] = 1;
                            pendingUpdates[`chats/${chatId}/receiverReadStatus`] = 0;
                        } else {
                            pendingUpdates[`chats/${chatId}/senderReadStatus`] = 0;
                            pendingUpdates[`chats/${chatId}/receiverReadStatus`] = 1;
                        }
                    } else {
                        pendingUpdates[`chats/${chatId}`] = {
                            chatId,
                            users: [uid1, uid2],
                            senderId: uid1,
                            receiverId: uid2,
                            senderReadStatus: 1,
                            receiverReadStatus: 0,
                            messages: [messageData],
                            lastMessage: "Proposed Booking Edit",
                            lastMessageTime: messageData.timestamp,
                            lastSenderId: uid1,
                        };
                    }
                }

                await update(ref(database), pendingUpdates);

                if (chatId) {
                    // Send SMS and Email to the renter
                    try {
                        const renterData = await getUserByID(uid2);
                        const ownerName = currentUser?.firstName && currentUser?.lastName 
                            ? `${currentUser.firstName} ${currentUser.lastName}` 
                            : "The boat owner";
                        const listingTitle = boatDetails?.title || "your booking";
                        
                        const renterSmsBody = `${ownerName} has proposed changes to your booking for ${listingTitle}. Please login to your dashboard to review and accept/decline the changes: ${process.env.REACT_APP_BASE_URL}`;
                        if (renterData?.phone) {
                            sendSMS(renterData.phone, renterSmsBody);
                        }

                        const emailSubject = "Proposed Changes to Your Booking";
                        const emailBody = `
                            <div style="font-family: Arial, sans-serif; color: #333;">
                                <h2>Booking Update Proposed</h2>
                                <p>Hi ${renterData?.firstName || 'Renter'},</p>
                                <p><strong>${ownerName}</strong> has proposed changes to your booking for <strong>${listingTitle}</strong>.</p>
                                <p>Please log in to your dashboard to review the new details and either accept or decline the changes.</p>
                                <br/>
                                <a href="${process.env.REACT_APP_BASE_URL}/dashboard/inbox" style="padding: 10px 20px; background-color: #007bff; color: white; text-decoration: none; border-radius: 5px;">View Proposed Changes</a>
                                <br/><br/>
                                <p>Thank you,<br/>BBR Support Team</p>
                            </div>
                        `;
                        if (renterData?.email) {
                            sendEmail(renterData.email, emailSubject, emailBody);
                        }

                        notifyRenterBookingEditProposed(
                          uid2,
                          listingTitle,
                          dynamicId,
                          uid1
                        ).catch((err) =>
                          console.error("Failed to create renter notification:", err)
                        );
                    } catch (notificationError) {
                        console.error("Error sending SMS/Email notifications:", notificationError);
                    }

                    // Trigger notification for the proposed edit
                    fetch(`${process.env.REACT_APP_BASE_URL}/schedule-message`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            senderId: uid1,
                            receiverId: uid2,
                            messageText: "Proposed Booking Edit",
                            chatId: chatId,
                            timestamp: proposedEdit.proposedAt,
                            lastSenderId: uid1
                        }),
                    }).catch(e => console.error("Notification failed", e));
                }

                toast.success("Proposed changes sent to renter for approval.");

            setModalStep(1);
            if (onUpdateSuccess) onUpdateSuccess();
            onHide();

        } catch (error) {
            console.error("Error executing direct update:", error);
            toast.error("Failed: " + (error.message || "Unknown error"));
        } finally {
            setDirectUpdateLoading(false);
        }
    };

    const handleCloseModal = () => {
        setModalStep(1);
        onHide();
    };


    return (
        <Modal show={show} onHide={handleCloseModal}>
            <Modal.Header closeButton>
                <Modal.Title>{modalStep === 1 ? "Edit Booking" : "Confirm Booking Update"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {modalStep === 1 && (
                    <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>Trip Date</Form.Label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Select Date"
                                value={newTripDate}
                                onChange={(newValue) => setNewTripDate(newValue)}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                                className="w-100"
                            />
                        </LocalizationProvider>
                    </Form.Group>

                    {/* TRIP PACKAGES */}
                    {tripPackages.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label>Trip Packages</Form.Label>
                            <Form.Select
                                value={selectedDurationOption?.type === 'package' ? JSON.stringify(selectedDurationOption) : ""}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        setSelectedDurationOption(JSON.parse(e.target.value));
                                        setCustomHours(0);
                                    } else if (selectedDurationOption?.type === 'package') {
                                        setSelectedDurationOption(null);
                                    }
                                }}
                            >
                                <option value="">Select Package</option>
                                {tripPackages.map((opt, idx) => (
                                    <option key={idx} value={JSON.stringify(opt)}>
                                        {opt.label} - ${opt.price}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {/* HOURLY PACKAGES */}
                    {hourlyPackages.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label>Hourly Packages</Form.Label>
                            <Form.Select
                                value={selectedDurationOption?.type === 'hourly' ? JSON.stringify(selectedDurationOption) : ""}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        setSelectedDurationOption(JSON.parse(e.target.value));
                                        setCustomHours(0);
                                    } else if (selectedDurationOption?.type === 'hourly') {
                                        setSelectedDurationOption(null);
                                    }
                                }}
                            >
                                <option value="">Select Hourly Option</option>
                                {hourlyPackages.map((opt, idx) => (
                                    <option key={idx} value={JSON.stringify(opt)}>
                                        {opt.label} - ${opt.price}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {/* DAILY PACKAGES (Overnight) */}
                    {dailyPackages.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label>Overnight Packages</Form.Label>
                            <Form.Select
                                value={selectedDurationOption?.type === 'daily' ? JSON.stringify(selectedDurationOption) : ""}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        setSelectedDurationOption(JSON.parse(e.target.value));
                                        setCustomHours(0);
                                    } else if (selectedDurationOption?.type === 'daily') {
                                        setSelectedDurationOption(null);
                                    }
                                }}
                            >
                                <option value="">Select Overnight Option</option>
                                {dailyPackages.map((opt, idx) => (
                                    <option key={idx} value={JSON.stringify(opt)}>
                                        {opt.label} - ${opt.price}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {/* Rent By Hours */}
                    {(hourlyPackages.length > 0 || boatDetails?.pricing?.hourlyRate) && (
                        <Form.Group className="mb-3">
                            <Form.Label>Rent By Hours</Form.Label>
                            <div className="d-flex align-items-center gap-3">
                                <Button variant="dark" onClick={() => {
                                    const newHours = Math.max(0, (customHours || 0) - 1);
                                    setCustomHours(newHours);
                                    if (newHours > 0) setSelectedDurationOption(null);
                                }}>
                                    <i className="material-icons">remove</i>
                                </Button>
                                <Form.Control
                                    type="number"
                                    value={customHours || ''}
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value) || 0;
                                        setCustomHours(val);
                                        if (val > 0) setSelectedDurationOption(null);
                                    }}
                                    style={{ width: '80px', textAlign: 'center' }}
                                />
                                <Button variant="dark" onClick={() => {
                                    const newHours = (customHours || 0) + 1;
                                    setCustomHours(newHours);
                                    if (newHours > 0) setSelectedDurationOption(null);
                                }}>
                                    <i className="material-icons">add</i>
                                </Button>
                            </div>
                            {customHours > 0 && (
                                <Form.Text className="text-muted">
                                    {customHours} hours selected. Estimated price: ${
                                        (Number(boatDetails?.pricing?.hourlyRate) || 0) * customHours
                                    }
                                </Form.Text>
                            )}
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Trip Time</Form.Label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                label="Select Time"
                                viewRenderers={{ hours: null, minutes: null, seconds: null }}
                                views={['hours', 'minutes']}
                                value={newTripTime}
                                onChange={(newValue) => setNewTripTime(newValue)}
                                className="w-100"
                            />
                        </LocalizationProvider>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="d-flex justify-content-between align-items-center">
                            <span>Passengers</span>
                            <span className={
                                ((passengerCounts.adults + passengerCounts.children + passengerCounts.infants) >= (Number(boatDetails?.capacity) || 0))
                                    ? "text-danger fw-bold"
                                    : "text-muted"
                            }>
                                Total: {passengerCounts.adults + passengerCounts.children + passengerCounts.infants} / Max: {boatDetails?.capacity}
                            </span>
                        </Form.Label>
                        <div className="d-flex gap-3">
                            <div>
                                <Form.Label><small>Adults</small></Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={passengerCounts.adults}
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const other = passengerCounts.children + passengerCounts.infants;
                                        if (val + other <= (Number(boatDetails?.capacity) || 100)) {
                                            setPassengerCounts({ ...passengerCounts, adults: val });
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <Form.Label><small>Children</small></Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={passengerCounts.children}
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const other = passengerCounts.adults + passengerCounts.infants;
                                        if (val + other <= (Number(boatDetails?.capacity) || 100)) setPassengerCounts({ ...passengerCounts, children: val });
                                    }}
                                />
                            </div>
                            <div>
                                <Form.Label><small>Infants</small></Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    value={passengerCounts.infants}
                                    onChange={(e) => {
                                        const val = Number(e.target.value);
                                        const other = passengerCounts.adults + passengerCounts.children;
                                        if (val + other <= (Number(boatDetails?.capacity) || 100)) setPassengerCounts({ ...passengerCounts, infants: val });
                                    }}
                                />
                            </div>
                        </div>
                    </Form.Group>

                    {(() => {
                        const startPrice = Number(bookingData.priceOffered);
                        let maxPassengers = Number(boatDetails.pricing?.maxPassengers) || 6;
                        if (selectedDurationOption?.raw?.maxPassengers !== undefined && selectedDurationOption?.raw?.maxPassengers !== "") {
                            maxPassengers = Number(selectedDurationOption.raw.maxPassengers);
                        }

                        let additionalPct = 0;
                        if (selectedDurationOption?.raw?.additionalPassengerPercentage !== undefined && selectedDurationOption?.raw?.additionalPassengerPercentage !== "") {
                            additionalPct = Number(selectedDurationOption.raw.additionalPassengerPercentage);
                        } else if (boatDetails?.boatType === "Fishing Charter") {
                            additionalPct = Number(boatDetails.pricing?.additionalPassengerPercentage) || 0;
                        }

                        const totalPax = (Number(passengerCounts.adults) || 0) + (Number(passengerCounts.children) || 0) + (Number(passengerCounts.infants) || 0);
                        let extraPax = Math.max(0, totalPax - maxPassengers);
                        if ((boatDetails?.boatType === "Bass Boat" || boatDetails?.boatType === "Fishing Charter") && selectedDurationOption?.type === "daily") {
                            extraPax = 0;
                        }
                        const factor = (additionalPct / 100);

                        let basePrice = 0;
                        if (selectedDurationOption) {
                            basePrice = Number(selectedDurationOption.price);
                        } else if (customHours > 0) {
                            basePrice = (Number(boatDetails?.pricing?.hourlyRate) || 0) * customHours;
                        } else {
                            const oldTotalPax = (bookingData.passengers?.total || 0);
                            const oldExtra = Math.max(0, oldTotalPax - maxPassengers);
                            basePrice = startPrice / (1 + (factor * oldExtra));
                        }

                        const newExtraPrice = (basePrice * factor * extraPax);

                        let platformFeeAmount = 0;
                        let taxAmount = 0;
                        let oldPlatformFeeAmount = 0;
                        let oldTaxAmountValUI = 0;
                        const priceChanged = Math.abs(Number(startPrice || 0) - Number(manualPrice || 0)) > 0.01;
                        
                        if (paymentDetails && paymentDetails.totalAmount) {
                            oldTaxAmountValUI = paymentDetails.totalTax ? (paymentDetails.totalTax / 100) : 0;
                            oldPlatformFeeAmount = Math.max(0, (paymentDetails.totalAmount / 100) - startPrice - oldTaxAmountValUI);
                            
                            if (priceChanged) {
                                const priceDelta = manualPrice - startPrice;
                                const newPlatformFeeDelta = calculateRenterFee(priceDelta, boatDetails);
                                const newTaxDelta = isTaxable ? (8.25 / 100) * priceDelta : 0;
                                
                                platformFeeAmount = Math.max(0, oldPlatformFeeAmount + newPlatformFeeDelta);
                                taxAmount = Math.max(0, oldTaxAmountValUI + newTaxDelta);
                            } else {
                                platformFeeAmount = oldPlatformFeeAmount;
                                taxAmount = oldTaxAmountValUI;
                            }
                        } else {
                            platformFeeAmount = calculateRenterFee(manualPrice, boatDetails);
                            taxAmount = isTaxable ? (8.25 / 100) * manualPrice : 0;
                            
                            oldPlatformFeeAmount = calculateRenterFee(startPrice, boatDetails);
                            oldTaxAmountValUI = isTaxable ? (8.25 / 100) * startPrice : 0;
                        }

                        const grandTotal = manualPrice + platformFeeAmount + taxAmount;
                        const oldGrandTotal = paymentDetails && paymentDetails.totalAmount ? (paymentDetails.totalAmount / 100) : (startPrice + oldPlatformFeeAmount + oldTaxAmountValUI);

                        const totalActuallyPaidCents = paymentDetails ? (paymentDetails.paidAmount || 0) + (paymentDetails.finalChargeAmount || 0) + ((paymentDetails.priceAdjustments || 0) * 100) : 0;
                        const paidAmount = totalActuallyPaidCents / 100;
                        
                        // delta is the new grand total minus the old grand total
                        const bookingPriceDelta = grandTotal - oldGrandTotal;

                        return (
                            <div className="mt-3 p-3 bg-light rounded border">
                                <h6 className="fw-bold">Price Details</h6>
                                {extraPax > 0 && additionalPct > 0 ? (
                                    <>
                                        <p className="mb-1 text-muted small">Base price (${basePrice.toFixed(2)}) includes up to {maxPassengers} passengers</p>
                                        <p className="mb-1 text-muted small">Additional passengers: {extraPax}</p>
                                        <p className="mb-1 text-muted small">Additional charge: ${newExtraPrice.toFixed(2)}</p>
                                        <hr className="my-2" />
                                    </>
                                ) : (
                                    <p className="mb-1 text-muted small">Base price: ${basePrice.toFixed(2)}</p>
                                )}
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="text-muted">Current Subtotal:</span>
                                    <span className="text-muted">${startPrice.toFixed(2)}</span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong>Estimated Subtotal:</strong>
                                    <Form.Control
                                        type="number"
                                        value={manualPrice}
                                        onChange={(e) => setManualPrice(Number(e.target.value))}
                                        style={{ width: '120px', textAlign: 'right', fontWeight: 'bold' }}
                                    />
                                </div>



                                {bookingPriceDelta > 0 && (
                                    <div className="mt-2 text-danger">
                                        <strong>Booking Price Difference:</strong> +${bookingPriceDelta.toFixed(2)}
                                    </div>
                                )}
                                {bookingPriceDelta < 0 && (
                                    <div className="text-success mt-1">
                                        <strong>Booking Price Difference:</strong> -${Math.abs(bookingPriceDelta).toFixed(2)}
                                    </div>
                                )}
                                {paymentDetails && totalActuallyPaidCents > 0 && (
                                    <div className="mt-3 pt-2 border-top">
                                        <div className="d-flex justify-content-between text-success">
                                            <span>Paid Amount:</span>
                                            <span>${paidAmount.toFixed(2)}</span>
                                        </div>
                                        {(() => {
                                            const balanceDue = Math.max(0, grandTotal - (totalActuallyPaidCents / 100));
                                            const isAdvanceBookingUI = 
                                                paymentDetails.advanceBooking === 1 || 
                                                paymentDetails.advanceBooking === true || 
                                                paymentDetails.remainingAmount > 0 || 
                                                paymentDetails.finalChargeAmount > 0 || 
                                                (paymentDetails.paidAmount < paymentDetails.totalAmount && paymentDetails.status === 'paid');
                                            
                                            return (
                                                <>
                                                    <div className={`d-flex justify-content-between fw-bold ${balanceDue > 0 ? "text-danger" : "text-muted"} mt-1`}>
                                                        <span>{balanceDue > 0 ? "Remaining Balance (Due):" : "Remaining Balance:"}</span>
                                                        <span>${balanceDue.toFixed(2)}</span>
                                                    </div>
                                                    {balanceDue > 0 && (
                                                        <small className="text-muted d-block mt-1 text-end fst-italic">
                                                            {isAdvanceBookingUI
                                                                ? (boatDetails?.fishingGuideDetails?.policies?.paymentTerms?.collectBalanceAtMeetup === true
                                                                    ? "*To be collected on meetup by Captain"
                                                                    : "*Remaining balance to be auto-collected by BBR 48 hours prior to trip")
                                                                : "*Balance will be charged immediately upon renter acceptance"}
                                                        </small>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                </Form>
                )}

                {modalStep === 2 && (() => {
                    return (
                    <div className="text-center py-4 px-3">
                        <>
                            <div style={{ fontSize: '3rem', color: '#1A1A1A', marginBottom: '10px' }}>
                                ⏳
                            </div>
                            <h4 className="fw-bold mb-3" style={{ color: '#1A1A1A' }}>
                                Renter Approval Required
                            </h4>
                            <p className="text-muted mb-0" style={{ fontSize: '1.1rem' }}>
                                {directUpdateData?.amountDifferenceCents < 0 && !directUpdateData?.hasOtherChanges 
                                    ? "This edit will reduce the booking price and issue a refund or lower the renter's remaining balance. The renter must accept these changes before they take effect."
                                    : "This edit modifies the booking details or increases the price. The renter must accept these changes before they take effect."}
                            </p>
                        </>
                        
                        {directUpdateData?.changes && directUpdateData.changes.length > 0 && (
                            <div className="text-start mt-4 bg-light p-3 rounded border shadow-sm mx-auto" style={{ maxWidth: '400px' }}>
                                <h6 className="fw-bold mb-3" style={{ color: '#1A1A1A', borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>Summary of Changes</h6>
                                <ul className="mb-0 text-muted" style={{ paddingLeft: '20px', fontSize: '0.95rem' }}>
                                    {directUpdateData.changes.map((c, i) => {
                                        const fieldLabel = c.field === "Duration" ? "Duration Change" : c.field;
                                        const cleanOld = typeof c.old === 'string' ? c.old.replace(/Night's/gi, "Days").replace(/Hour's/gi, "Hours").replace(/'s/g, "s") : c.old;
                                        const cleanNew = typeof c.new === 'string' ? c.new.replace(/Night's/gi, "Days").replace(/Hour's/gi, "Hours").replace(/'s/g, "s") : c.new;
                                        return (
                                            <li key={i} className="mb-2">
                                                <strong>{fieldLabel}:</strong> {cleanOld} ➔ {cleanNew}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>
                    );
                })()}
            </Modal.Body>
            <Modal.Footer>
                {modalStep === 1 ? (
                    <>
                        <Button variant="secondary" onClick={handleCloseModal} style={{ backgroundColor: '#fceabb', color: '#1A1A1A', border: 'none' }}>Close</Button>
                        <Button variant="primary" onClick={handleUpdateBooking} disabled={editLoading} style={{ backgroundColor: '#1A1A1A', color: '#fff', border: 'none' }}>
                            {editLoading ? "Saving..." : "Save Changes"}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="secondary" onClick={() => setModalStep(1)} disabled={directUpdateLoading} style={{ backgroundColor: '#fceabb', color: '#1A1A1A', border: 'none' }}>
                            Back
                        </Button>
                        <Button variant="primary" onClick={executeFinalUpdate} disabled={directUpdateLoading} style={{ backgroundColor: '#1A1A1A', color: '#fff', border: 'none' }}>
                            {directUpdateLoading ? "Processing..." : "Send to Renter"}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}
